import React, { useContext, useState } from 'react'
import Alert from '@mui/material/Alert'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import AddCommentIcon from '@mui/icons-material/AddComment'
import API from '../../../services/API'
import moment from 'moment'
import { UserContext } from '../../../App'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import SmallModal from '../../../components/Modals/SmallModal'
import DetailsBox from '../../../components/Cards/DetailsBox'
import { EventMetaContext, PlannerCommentsContext } from './SingleEvent'

const InternalNotes = () => {
  const { eventMeta, loadEvent } = useContext(EventMetaContext)
  const { plannerComments } = useContext(PlannerCommentsContext)
  const comments = plannerComments
  const [newComment, setNewComment] = useState('')
  const [addToCommandLog, setAddToCommandLog] = useState(false)
  const { user } = useContext(UserContext)
  const [openAddComment, setOpenAddComment] = useState(false)

  const handleDeleteComment = (commentID, index) => {
    // TODO
    API.post('/delete-event-comment-internal', {
      eventID: eventMeta._id,
      commentID,
    })
      .then((res) => {
        if (res.status === 200) {
          window.location.reload()
        }
      })
      .catch((e) => console.log(e))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    API.post('/event-comment-internal', {
      eventID: eventMeta._id,
      newComment,
      addToCommandLog,
    })
      .then((res) => {
        if (res.status === 200) {
          loadEvent()
          setNewComment('')
          setOpenAddComment(false)
        }
      })
      .catch((e) => console.log(e))
  }

  const SingleComment = ({ comment }) => {
    const [anchorEl, setAnchorEl] = useState(null)
    const commentOwner = comment.by?._id === user._id
    const isAdmin =
      user.employee?.teams.includes('Event Planning') || user?.security?.isAdmin

    return (
      <Box
        display="flex"
        gap={1}
        border="solid 1px #D3D3D3"
        borderRadius={2}
        padding={1}
        sx={{ position: 'relative' }}
        bgcolor="white"
      >
        <Avatar src={comment.by?.photograph} sx={{ width: 24, height: 24 }} />
        <Box display="flex" flexDirection="column">
          <Typography variant="title3" fontSize="0.9rem" lineHeight={1}>
            {comment.by?.firstName} {comment.by?.lastName}
          </Typography>

          <Typography variant="caption">
            {moment(comment.date).format('Do MMM YYYY - HH:mm')}
          </Typography>
          {/* System notes (SMS broadcasts) put the message on its own line */}
          <Typography sx={{ whiteSpace: 'pre-line', overflowWrap: 'anywhere' }}>
            {comment.comment}
          </Typography>
        </Box>
        {(commentOwner || isAdmin) && (
          <Box sx={{ position: 'absolute', top: '5px', right: '5px' }}>
            <IconButton
              sx={{ borderRadius: 2, padding: '2px' }}
              onClick={(e) => setAnchorEl(e.currentTarget)}
            >
              <MoreHorizIcon color="primary" />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={!!anchorEl}
              onClose={() => setAnchorEl(null)}
            >
              <MenuItem onClick={() => handleDeleteComment(comment._id)}>
                Delete
              </MenuItem>
            </Menu>
          </Box>
        )}
      </Box>
    )
  }

  return (
    <Box
      component={Paper}
      p={2}
      elevation={0}
      sx={{
        borderRadius: 3,
        border: '1px solid rgba(15, 23, 42, 0.08)',
        boxShadow: '0 8px 24px rgba(15, 23, 42, 0.05)',
        bgcolor: 'background.paper',
        width: '100%',
      }}
    >
      <Stack
        direction="row"
        gap={1}
        alignItems="center"
        justifyContent="space-between"
      >
        <Stack gap={0.25}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Internal Notes
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Visible to event planning staff only
          </Typography>
        </Stack>
        <IconButton
          size="small"
          color="primary"
          onClick={() => setOpenAddComment(true)}
        >
          <Tooltip title="Add a Note">
            <AddCommentIcon fontSize="small" />
          </Tooltip>
        </IconButton>
      </Stack>
      <Box
        display="flex"
        flexDirection="column"
        gap={1}
        minHeight="100%"
        overflow="auto"
        justifyContent="stretch"
      >
        <Box>
          <Box
            display="flex"
            flexDirection="column"
            flex={1}
            gap={1}
            maxHeight={300}
            overflow="auto"
          >
            {comments
              .sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0))
              .map((comment) => (
                <SingleComment comment={comment} key={comment._id} />
              ))}
          </Box>
        </Box>

        <SmallModal
          open={openAddComment}
          onClose={() => setOpenAddComment(false)}
          style={{ width: 400, maxWidth: '80vw' }}
        >
          <Stack component="form" onSubmit={handleSubmit} gap={1}>
            <Typography variant="title3">{eventMeta.title}</Typography>
            <TextField
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Add an Internal Note..."
              size="small"
              fullWidth
              multiline
              minRows={2}
              required
            />
            <FormControlLabel
              label="Add to Command Log"
              control={
                <Checkbox
                  checked={addToCommandLog}
                  onChange={(e) => setAddToCommandLog(e.target.checked)}
                />
              }
            />
            <Button
              type="submit"
              fullWidth
              color="primary"
              variant="contained"
              size="small"
              sx={{ marginTop: 1 }}
            >
              Submit
            </Button>
          </Stack>
        </SmallModal>
      </Box>
    </Box>
  )
}

export default React.memo(InternalNotes)
